import { useDynamicForm } from "@/components/dynamic-form/hook/useDynamicForm";
import { enhanceEndTime } from "@/components/dynamic-form/formatter/enhanceEndTime";
import { PageContainer } from "@/components/page-container";
import { TableContainer } from "@/components/table-container";
import { DynamicForm } from "@/components/dynamic-form";
import { getInitialValues } from "@/components/dynamic-form/helper/getInitialValues";
import { useSearchConfig } from "@/pages/consumption/view/billing-details/view/picture-member/hooks/useSearchConfig";
import { useWatchPagination } from "@/components/dynamic-form/hook/useWatchPagination";
import { useColumns } from "@/pages/consumption/view/billing-details/view/picture-member/hooks/useColumns";
import { Table, Popover } from "@renderbus/ui";
import { useCustomColumns } from "project-config/hooks/useCustomColumns";
import { useFilterColumns } from "project-config/hooks/useFilterColumns";
import {
  OperationArea,
  SearchAreaWrapper,
} from "@/pages/consumption/components/atom";
import { ColumnCheckboxes } from "@/pages/consumption/components/column-checkboxes";
import { OperationButton } from "@/components/operation-button";
import { useTranslation } from "react-i18next";
import { ExportBillForm } from "@/pages/consumption/components/export-form/export-bill-form";
import { Modal } from "@/components/renderbus-modal";
import { useExportBill } from "@/pages/consumption/hooks/useExportBill";
import { useEffect } from "react";
import {
  BillDetailsTabsKey,
  BillTabTypeEnum,
} from "@/pages/consumption/constants";
import { useConsumeStore } from "@/pages/consumption/store/ConsumeStore";

export function PictureMember() {
  const {
    form,
    tableData,
    fetchData,
    pageNum,
    setPageNum,
    total,
    pageSize,
    getSearchParams,
  } = useDynamicForm({
    api: "/api/rendering/pay/consume/bill/getPictureMemberConsumeList",
    defaultPageSize: 10,
    formatter: {
      time: enhanceEndTime,
    },
  });

  useWatchPagination(pageSize, pageNum, fetchData);

  const { t } = useTranslation("center");

  const { searchConfig } = useSearchConfig();
  const { columns } = useColumns();
  const { billDetailsActiveKey, activeKey } = useConsumeStore();

  const { checkedValue, onChange, defaultCheckData } = useCustomColumns(
    columns,
    "consumption_task_picture"
  );
  const { columns: filteredColumns } = useFilterColumns(columns, checkedValue);

  const { openModal, setModal, exportForm, onOk } = useExportBill({
    api: "/api/rendering/task/export/exportPictureMemberConsumeList",
    searchParams: getSearchParams({ hiddenPageSize: true }),
  });

  useEffect(() => {
    if (
      activeKey === BillTabTypeEnum.BillingDetails &&
      billDetailsActiveKey === BillDetailsTabsKey.PictureMember
    ) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billDetailsActiveKey, activeKey]);

  return (
    <PageContainer>
      <TableContainer>
        <SearchAreaWrapper>
          <DynamicForm
            initialValues={getInitialValues(searchConfig)}
            searchConfig={searchConfig}
            searchForm={form}
            onSearch={() =>
              fetchData({
                extraData: {
                  pageNum: 1,
                },
              })
            }
          />

          <OperationArea>
            <Popover
              content={
                <ColumnCheckboxes
                  columns={columns}
                  onChange={onChange}
                  value={checkedValue}
                  defaultValue={defaultCheckData}
                />
              }
            >
              <>
                <OperationButton text={t("consumption_custom_column")} />
              </>
            </Popover>

            <OperationButton
              text={t("consumption_export")}
              onClick={() => setModal(true)}
            />
          </OperationArea>
        </SearchAreaWrapper>

        <Table
          paginationCurrent={pageNum}
          columns={filteredColumns}
          dataSource={tableData}
          gap={false}
          total={total}
          defaultPageSize={pageSize}
          onPageNumChange={async (num: number) => {
            setPageNum(num);
          }}
        />
      </TableContainer>

      <Modal
        title={t("user_consume_pick_output_dialog_title")}
        open={openModal}
        onOk={onOk}
        onCancel={() => {
          setModal(false);
          exportForm.resetFields();
        }}
      >
        <ExportBillForm form={exportForm} />
      </Modal>
    </PageContainer>
  );
}
